import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { AccountInfo, BrowserAuthError, EventMessage, EventType, IPublicClientApplication } from "@azure/msal-browser";
import {
    AuthType,
    msalInstanceAzureAD,
    msalInstanceB2C,
    getActiveMsalInstance,
    getActiveAuthType,
    setActiveAuthType,
    clearActiveAuthType,
    azureADConfig,
    b2cConfig,
    b2cEnabled
} from "../authConfig";

interface AuthContextValue {
    isActiveUser: boolean;
    isMsalInitialized: boolean;
    isLoggingOut: boolean;
    forceLogout: () => Promise<void>;
    reauthenticate: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
    children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [isActiveUser, setIsActiveUser] = useState(false);
    const [isMsalInitialized, setIsMsalInitialized] = useState(false);
    const [isLoggingOut, _setIsLoggingOut] = useState(false);

    // Mirror isLoggingOut in a ref so MSAL event callbacks (registered once) always
    // read the current value rather than the stale closure value.
    const isLoggingOutRef = useRef(false);
    const setIsLoggingOut = (val: boolean) => {
        isLoggingOutRef.current = val;
        _setIsLoggingOut(val);
    };

    // Promote the given account on its instance and mark the matching auth type active.
    // B2C handling is kept but feature-flagged off via b2cEnabled until the client
    // is ready; while disabled, B2C sessions are not restored and cannot sign in.
    const syncActiveAccount = (instance: IPublicClientApplication, authType: AuthType, account?: AccountInfo | null) => {
        if (account) {
            instance.setActiveAccount(account);
            setActiveAuthType(authType);
            setIsActiveUser(true);
        } else {
            instance.setActiveAccount(null);
            setIsActiveUser(false);
        }
    };

    // Log out without a popup. Clears the local MSAL cache via logoutRedirect with
    // onRedirectNavigate returning false, which wipes tokens without navigating the page.
    // The Azure AD SSO session remains active so users re-authenticate silently next visit.
    const forceLogout = async () => {
        setIsLoggingOut(true);
        const instance = getActiveMsalInstance();
        try {
            await instance.logoutRedirect({
                account: instance.getActiveAccount(),
                onRedirectNavigate: () => false
            });
        } catch {
            // Navigation may throw when prevented; state is still cleaned up below.
        }
        // Ensure state is cleared whether or not LOGOUT_SUCCESS fires.
        clearActiveAuthType();
        setIsActiveUser(false);
        setIsLoggingOut(false);
    };

    // Interactive re-auth on the active instance after silent refresh has failed.
    // Must be invoked from a user gesture or popup blockers will eat the window.
    // Returns true on success; false if the popup was blocked or dismissed.
    const reauthenticate = async (): Promise<boolean> => {
        const authType: AuthType = getActiveAuthType() ?? "azuread";
        const instance = authType === "b2c" ? msalInstanceB2C : msalInstanceAzureAD;
        const config = authType === "b2c" ? b2cConfig : azureADConfig;
        const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];

        try {
            if (account) {
                const result = await instance.acquireTokenPopup({
                    ...config.tokenRequest,
                    account,
                    redirectUri: "/redirect"
                });
                syncActiveAccount(instance, authType, result.account);
                return true;
            }
        } catch (error) {
            if (error instanceof BrowserAuthError && error.errorCode === "user_cancelled") {
                return false;
            }
            console.error("Popup token re-acquisition failed, falling back to login popup:", error);
        }

        try {
            const result = await instance.loginPopup({
                ...config.loginRequest,
                redirectUri: "/redirect",
                loginHint: account?.username
            });
            syncActiveAccount(instance, authType, result.account ?? null);
            return true;
        } catch (error) {
            console.error("Interactive re-authentication failed:", error);
            return false;
        }
    };

    useEffect(() => {
        let isMounted = true;

        const makeCallback = (instance: IPublicClientApplication, authType: AuthType) => (event: EventMessage) => {
            if (event.eventType === EventType.LOGOUT_START) {
                // Protect Chat from unmounting while the logout is still in progress.
                setIsLoggingOut(true);
            }

            if (event.eventType === EventType.LOGOUT_SUCCESS) {
                setIsLoggingOut(false);
                if (isMounted) {
                    setIsActiveUser(false);
                }
            }

            if (event.eventType === EventType.LOGOUT_FAILURE) {
                // Popup was dismissed or failed — account is still valid; do not navigate.
                setIsLoggingOut(false);
            }

            if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
                const result = event.payload as { account?: AccountInfo };
                if (isMounted) {
                    syncActiveAccount(instance, authType, result.account ?? null);
                }
            }
        };

        const azureADCallbackId = msalInstanceAzureAD.addEventCallback(makeCallback(msalInstanceAzureAD, "azuread"));
        const b2cCallbackId = msalInstanceB2C.addEventCallback(makeCallback(msalInstanceB2C, "b2c"));

        const initAuth = async () => {
            try {
                // Handle any redirect-based response (harmless no-op for the popup flow).
                await msalInstanceAzureAD.handleRedirectPromise();

                // While B2C is disabled, drop any stale stored B2C auth type so it is
                // not honoured if the feature is enabled again later.
                if (!b2cEnabled && localStorage.getItem("auth_type") === "b2c") {
                    clearActiveAuthType();
                }

                // Restore an existing session: prefer Azure AD, fall back to a B2C
                // account only when the B2C feature is enabled.
                const azureADAccounts = msalInstanceAzureAD.getAllAccounts();
                const b2cAccounts = b2cEnabled ? msalInstanceB2C.getAllAccounts() : [];

                if (isMounted) {
                    if (azureADAccounts.length > 0) {
                        syncActiveAccount(msalInstanceAzureAD, "azuread", azureADAccounts[0]);
                    } else if (b2cAccounts.length > 0) {
                        syncActiveAccount(msalInstanceB2C, "b2c", b2cAccounts[0]);
                    }
                }
            } catch (error) {
                console.error("MSAL initialization failed.", error);
                if (isMounted) {
                    setIsActiveUser(false);
                }
            } finally {
                if (isMounted) {
                    setIsMsalInitialized(true);
                }
            }
        };

        initAuth();

        return () => {
            isMounted = false;
            if (azureADCallbackId) {
                msalInstanceAzureAD.removeEventCallback(azureADCallbackId);
            }
            if (b2cCallbackId) {
                msalInstanceB2C.removeEventCallback(b2cCallbackId);
            }
        };
    }, []);

    return (
        <AuthContext.Provider value={{ isActiveUser, isMsalInitialized, isLoggingOut, forceLogout, reauthenticate }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuthContext = (): AuthContextValue => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuthContext must be used within an AuthProvider");
    }
    return context;
};
